/**
 * Labels of ERA concepts (http://data.europa.eu/949/concepts/...) for RINF values, read once
 * from /semantics-config/era-labels.json (scripts/build-era-labels.mjs): the ERA vocabularies are
 * in no DIM dataset. ERA properties (the not-yet-available lists) take the labels of the RINF status
 * file, and other era: values (bodies, organisation roles) their label from rinf-bn.
 * Without either, they keep their prefixed names.
 */

import { useEffect, useState } from 'react';
import type { ResolvedDisplayValues } from '@hooks/useResolvedDisplayValues';
import { rinfStatusFileUrl } from '@utils/rinfStatus.utils';

const ERA_LABELS_URL = '/semantics-config/era-labels.json';

type EraLabels = Record<string, Record<string, string>>;

const labelsPromise = new Map<string, Promise<ResolvedDisplayValues>>();

/** A short code such as "B" reads better with the start of its definition. */
function display(entry: Record<string, string>, language: string): string | null {
  const label = entry[language] ?? entry.en ?? Object.values(entry).find((value) => value !== entry.definition);
  if (!label) return null;
  if (label.length > 3 || !entry.definition) return label;
  const definition = entry.definition.replace(/\.$/, '');
  return `${label} · ${definition.length > 60 ? `${definition.slice(0, 57)}…` : definition}`;
}

/** Every { iri, label } in the RINF status file: its classes, properties and subsystems. */
function statusLabels(node: unknown, into: ResolvedDisplayValues): ResolvedDisplayValues {
  if (Array.isArray(node)) node.forEach((item) => statusLabels(item, into));
  else if (node && typeof node === 'object') {
    const { iri, label } = node as { iri?: unknown; label?: unknown };
    if (typeof iri === 'string' && typeof label === 'string' && !into[iri]) into[iri] = [label];
    Object.values(node).forEach((value) => statusLabels(value, into));
  }
  return into;
}

function load(language: string): Promise<ResolvedDisplayValues> {
  const cached = labelsPromise.get(language);
  if (cached) return cached;
  const status = fetch(rinfStatusFileUrl())
    .then((response) => (response.ok && response.headers.get('Content-Type')?.includes('json') ? response.json() : null))
    .then((data) => statusLabels(data, {}))
    .catch(() => ({}));
  const loading = fetch(ERA_LABELS_URL)
    .then((response) => (response.ok && response.headers.get('Content-Type')?.includes('json') ? response.json() : {}))
    .then((labels: EraLabels) => {
      const resolved: ResolvedDisplayValues = {};
      for (const [iri, entry] of Object.entries(labels)) {
        const text = display(entry, language);
        if (text) resolved[iri] = [text];
      }
      return resolved;
    })
    .catch(() => ({}))
    .then(async (resolved) => ({ ...(await status), ...resolved }));
  labelsPromise.set(language, loading);
  return loading;
}

export function useEraLabels(language: string): ResolvedDisplayValues {
  const [labels, setLabels] = useState<ResolvedDisplayValues>({});
  useEffect(() => {
    let cancelled = false;
    load(language.split('-')[0]).then((result) => !cancelled && setLabels(result));
    return () => {
      cancelled = true;
    };
  }, [language]);
  return labels;
}

const RINF_LABELS = (iris: string[]) => `PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
PREFIX skos: <http://www.w3.org/2004/02/skos/core#>
PREFIX era: <http://data.europa.eu/949/>
SELECT ?iri (SAMPLE(?text) AS ?label) WHERE {
  VALUES ?iri { ${iris.map((iri) => `<${iri}>`).join(' ')} }
  { ?iri rdfs:label|skos:prefLabel ?text } UNION { ?iri era:roleOf/rdfs:label ?text }
} GROUP BY ?iri`;

const rinfLabels = new Map<string, Promise<string | null>>();

/** Labels from rinf-bn for era: IRIs the vocabulary file does not have. */
export function useRinfLabels(iris: string[], known: ResolvedDisplayValues): ResolvedDisplayValues {
  const [labels, setLabels] = useState<ResolvedDisplayValues>({});
  const key = iris.filter((iri) => !known[iri]).sort().join(' ');
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const list = key.split(' ');
    const missing = list.filter((iri) => !rinfLabels.has(iri));
    if (missing.length > 0) {
      const batch = fetch('/sparql-rinf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/sparql-results+json' },
        body: new URLSearchParams({ query: RINF_LABELS(missing) }),
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((body) => {
          const found = new Map<string, string>();
          for (const row of body?.results.bindings ?? []) found.set(row.iri.value, row.label.value);
          return found;
        })
        .catch(() => new Map<string, string>());
      for (const iri of missing) rinfLabels.set(iri, batch.then((found) => found.get(iri) ?? null));
    }
    Promise.all(list.map((iri) => rinfLabels.get(iri)!.then((label) => [iri, label] as const))).then((entries) => {
      if (cancelled) return;
      setLabels(Object.fromEntries(entries.filter(([, label]) => label).map(([iri, label]) => [iri, [label!]])));
    });
    return () => {
      cancelled = true;
    };
  }, [key]);
  return labels;
}
